// Word-order-independent matching for every picker in the app.
//
// WHY. People do not remember which way round a name is stored. "Sanjay Ailani"
// and "Ailani Sanjay" are the same person, and the database holds one of them —
// so a substring filter (the default in MUI's Autocomplete) matches whichever
// order happens to be stored and silently finds NOTHING for the other. The user
// then concludes the person is not in the list.
//
// The rule here: split the query into words, and match when EVERY word appears
// somewhere in the option — in any order, as a prefix of any word of it.
//
//   query "sanjay ailani"  vs  "Ailani Sanjay Hareshlal"   -> match
//   query "ailani sanjay"  vs  "Ailani Sanjay Hareshlal"   -> match
//   query "ail san"        vs  "Ailani Sanjay Hareshlal"   -> match
//   query "sanjay patel"   vs  "Ailani Sanjay Hareshlal"   -> no match
//
// The same applies to devices ("touch 4" finding "4 TOUCH") and to anything
// else searched by name, which is why this lives in core rather than in one
// screen.

/** Lowercase words, punctuation and separators discarded. */
export const searchTokens = (text) => String(text ?? '')
  .toLowerCase()
  .split(/[^a-z0-9]+/i)
  .filter(Boolean);

/**
 * Does `haystack` match `query`, ignoring word order?
 *
 * An empty query matches everything — a picker that has not been typed into
 * shows its whole list.
 */
export function matchesTokens(haystack, query) {
  const wanted = searchTokens(query);
  if (!wanted.length) return true;
  const have = searchTokens(haystack);
  if (!have.length) return false;
  // Prefix rather than equality, so partial words work while typing.
  return wanted.every((w) => have.some((h) => h.startsWith(w))
    // Substring as a fallback: "4811" should still find "884811", and a unique
    // number is one long token with no word boundaries to prefix-match on.
    || have.some((h) => h.includes(w)));
}

/**
 * A `filterOptions` for MUI Autocomplete.
 *
 * `textOf` returns everything searchable about an option — for a user that is
 * the name AND the mobile number, so either finds them.
 */
export const tokenFilter = (textOf) => (options, { inputValue }) =>
  options.filter((o) => matchesTokens(textOf(o), inputValue));
